import { Component, computed, effect, inject } from '@angular/core';
import {
  BuildingDetailDto,
  BuildingFilterDto,
  ConditionDto,
  InspectionDetailDto,
  InspectionFilterDto,
  InspectionItemDto,
  InspectionService, RegionItemDto, TeamItemDto,
  TenantDetailDto,
  TenantService,
} from '@artemis-software/wr-api';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { general_tab } from '@pages/inspection-detail-page-v2/inspection-tab.constants';
import { toSelectedRange } from '@/utils/filter-range-utils';
import { DateRangeObject } from '@models/date-range';
import { currentTenantId, isInternalTenant } from '@/utils/admin-utils';
import ConditionEnum = ConditionDto.ConditionEnum;
import { userToIdReference } from '@/utils/id-reference';
import { BehaviorSubject } from 'rxjs';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-inspection-overview-page',
  templateUrl: './inspection-overview-page.component.html',
  styleUrls: ['./inspection-overview-page.component.scss'],
})
export class InspectionOverviewPageComponent {

  readonly inspectionService = inject(InspectionService);
  readonly tenantService = inject(TenantService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly currentUserService = inject(CurrentUserService);

  canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateInspections'));
  tenantId = currentTenantId();

  readonly loadInspections = (filter: InspectionFilterDto) =>
    this.inspectionService.findAll({ ...filter, selectedRange: toSelectedRange(filter.selectedRange), internalTenantId: this.tenantId() });
  readonly getCount = (filter: InspectionFilterDto) =>
    this.inspectionService.getCount({ ...filter, selectedRange: toSelectedRange(filter.selectedRange), internalTenantId: this.tenantId() });

  tenantFilter = new BehaviorSubject<string[]>([]);
  tenantFilter$ = this.tenantFilter.asObservable();

  currentTenantId = currentTenantId();
  isInternalTenant = isInternalTenant();
  form!: FormGroup;

  constructor() {
    this.initFilter();
    this.triggerFilter();
  }

  initFilter(): void {
    const filter = localStorage.getItem('internalInspectionFilter');
    if (filter) {
      const filterValue = JSON.parse(filter);
      this.initFilterForm(filterValue.text);
      this.addSpecialFields(
        filterValue.tenantId,
        filterValue.building,
        filterValue.status,
        filterValue.technician,
        filterValue.clerk,
        filterValue.selectedRange ?? undefined,
        filterValue.condition,
        filterValue.inspectionType,
      );
    } else {
      this.initFilterForm('');
    }

  }

  readonly inspectionTypeExportValue = (inspection: InspectionItemDto): string =>
    inspection.inspectionType === InspectionDetailDto.InspectionTypeEnum.Schadstellenplan ? 'Schadstellenplan' : 'Begehung';

  initFilterForm(text: string): void {
    this.form = this.formBuilder.group({
      fullTextSearch: this.formBuilder.control(text),
      tenantId: this.formBuilder.control(null),
      isPropertyManager: this.formBuilder.control(false),
      continuationNumber: this.formBuilder.control(undefined),
      building: this.formBuilder.control(undefined),
      buildingId: this.formBuilder.control(null),
      technicianReference: this.formBuilder.control(null),
      clerkReference: this.formBuilder.control(null),
      status: this.formBuilder.control(InspectionDetailDto.StatusEnum.None),
      inspectionType: this.formBuilder.control(undefined),
      selectedRange: new FormControl(null),
      condition: this.formBuilder.control(undefined),
      technician: this.formBuilder.control(null),
      clerk: this.formBuilder.control(null),
      region: this.formBuilder.control<RegionItemDto | null>(null),
      regionId: this.formBuilder.control<string | null>(null),
      team: this.formBuilder.control<TeamItemDto | null>(null),
      teamId: this.formBuilder.control<string | null>(null),
    });

    this.form.get('region')?.valueChanges.subscribe((region) => {
      if (region) {
        this.form.get('regionId')?.setValue(region.id);
      } else {
        this.form.get('regionId')?.setValue(null);
      }
    });

    this.form.get('team')?.valueChanges.subscribe((team) => {
      if (team) {
        this.form.get('teamId')?.setValue(team.id);
      } else {
        this.form.get('teamId')?.setValue(null);
      }
    });

    this.form.get('tenantId')?.valueChanges.subscribe(() => {
      this.form.get('building')?.setValue(undefined);
    });

    this.form.get('technician')?.valueChanges.subscribe((value) => {
      this.form.get('technicianReference')?.setValue(userToIdReference(value));
    });
    this.form.get('clerk')?.valueChanges.subscribe((value) => {
      this.form.get('clerkReference')?.setValue(userToIdReference(value));
    });

    this.form.get('building')?.valueChanges.subscribe((building) => {
      if (building?.id) {
        this.form.get('buildingId')?.setValue(building.id);
      } else {
        this.form.get('buildingId')?.setValue(null);
      }
    });
    effect( () => {
      const isInternalTenant = this.isInternalTenant();
      const currentTenantId = this.currentTenantId();
      if (currentTenantId && isInternalTenant) {
        this.tenantFilter.next([currentTenantId]);
      }
    });
  }

  async addSpecialFields(tenantId: string, building: BuildingDetailDto, status: string, technicianId: string,
    clerkId: string, selectedRange: DateRangeObject | undefined, condition: ConditionEnum, inspectionType?: InspectionDetailDto.InspectionTypeEnum): Promise<void> {
    this.form.patchValue({
      'inspectionType': inspectionType,
      'tenantId': tenantId,
      'building': building,
      'technicianReference': technicianId,
      'clerkReference': clerkId,
      'selectedRange': selectedRange,
      'condition': condition,
    });
  }

  navigateToInspection(entry: InspectionItemDto): void {
    this.router.navigate(['inspection', entry.id, general_tab]);
  }

  triggerFilter(): void {
    this.form.controls['buildingId'].setValue(this.form.controls['building'].value?.id);
    const filter: InspectionFilterDto = this.form.value;
    localStorage.setItem('internalInspectionFilter', JSON.stringify(filter));
  }

  add(): void {
    this.router.navigate(['inspection', 'new']);
  }

  protected readonly BuildingFilterDto = BuildingFilterDto;
  protected readonly TenantTypeEnum = TenantDetailDto.TypeEnum;
}


